import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import Loaders from "components/Common/Loaders";
import ServerListPagination from "components/Common/ServerListPagination";
import SimpleNotificationListItem from "components/Common/SimpleNotificationListItem";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import {
  fetchUserNotificationMetadata,
  markUserNotificationAsRead,
  submitUserNotificationFilterData,
} from "model/UserNotificationModel";
import {
  extractNotificationListFromResponse,
  mapNotificationApiList,
} from "helpers/notificationListHelpers";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";


const SimpleAllNotifications = () => {
  const navigate = useNavigate();
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMetaData, setFilterMetaData] = useState([]);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const unreadCount = useMemo(
    () => notifications.filter((item) => item.unread).length,
    [notifications]
  );

  const getNotificationList = async (payloadOverrides = {}) => {
    setLoading(true);
    try {
      const payload = {
        ...requestPayload,
        ...payloadOverrides,
      };
      const response = await submitUserNotificationFilterData(payload);
      const { records, pageNumber, pageSize, totalPages, totalRecords } =
        extractNotificationListFromResponse(response);

      setNotifications(mapNotificationApiList(records));
      setPaginationMeta({
        pageNumber: Number(pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(totalPages) || 0,
        totalRecords: Number(totalRecords) || records.length,
      });
      setRequestPayload(payload);
    } catch (err) {
      toast.error(err?.message || "Unable to load notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:userNotifications", () =>
          fetchUserNotificationMetadata({})
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        setFilterMetaData(Array.isArray(meta?.filters) ? meta.filters : []);
      } catch (e) {
        toast.error(e?.message || "Unable to load notification metadata");
        setFilterMetaData([]);
      }

      await getNotificationList({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    };
    bootstrap();
  }, []);

  const handleSearchSubmit = async (value) => {
    const trimmed = String(value ?? "").trim();
    await getNotificationList({
      pageNumber: 1,
      nameContains: trimmed || null,
      search: trimmed || null,
    });
  };

  const handleFilterSubmit = async (data = {}) => {
    await getNotificationList({
      ...data,
      pageNumber: 1,
      nameContains: searchQuery.trim() || data?.nameContains || null,
    });
    setCommonFilterModalOpen(false);
  };

  const handlePageChange = async (page) => {
    await getNotificationList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getNotificationList({ pageNumber: 1, pageSize: rows });
  };

  const markNotificationReadLocally = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((item) =>
        item.id === notificationId
          ? { ...item, read: true, unread: false, isRead: true }
          : item
      )
    );
  }, []);

  const handleNotificationClick = useCallback(
    async (item = {}) => {
      const notificationId = String(item?.id ?? "").trim();
      if (!notificationId) return;

      if (item?.unread) {
        try {
          await markUserNotificationAsRead(notificationId);
          markNotificationReadLocally(notificationId);
        } catch {
          return;
        }
      }

      const link = String(item?.link ?? "").trim();
      if (link) {
        if (/^https?:\/\//i.test(link)) {
          window.open(link, "_blank", "noopener,noreferrer");
        } else {
          navigate(link.startsWith("/") ? link : `/${link}`);
        }
      }
    },
    [markNotificationReadLocally, navigate]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Card className="border">
          <CardBody>
            <div className="notification-header pt-0 px-0 border-0">
              <h5 className="mb-1">Notifications</h5>
              <small className="text-muted">
                {unreadCount
                  ? `You have ${unreadCount} unread message${unreadCount === 1 ? "" : "s"}`
                  : `${paginationMeta.totalRecords} notification${
                      paginationMeta.totalRecords === 1 ? "" : "s"
                    }`}
              </small>
            </div>

            <Row className="align-items-center g-2 mt-3 mb-3">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search notifications"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />
                    <Button
                      className="search-icon-button"
                      type="button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search" aria-hidden />
                    </Button>
                  </div>
                </Col>
                {/* {filterMetaData?.length ? (
                  <Col xs="12" md="4" className="text-md-end">
                    <Button
                      color="light"
                      className="border"
                      type="button"
                      onClick={() => setCommonFilterModalOpen(true)}
                    >
                      <i className="fa fa-filter me-1" aria-hidden />
                      Filters
                    </Button>
                  </Col>
                ) : null} */}
            </Row>

            <div className="simple-all-notifications-list simple-notification-feed border rounded overflow-hidden">
              {loading ? (
                <Loaders />
              ) : notifications.length ? (
                notifications.map((item) => (
                  <SimpleNotificationListItem
                    key={item.id}
                    item={item}
                    onClick={handleNotificationClick}
                  />
                ))
              ) : (
                <div className="text-center text-muted py-4">No notifications</div>
              )}
              {!loading ? (
                <ServerListPagination
                  currentPage={paginationMeta.pageNumber}
                  rowsPerPage={paginationMeta.pageSize}
                  totalPages={paginationMeta.totalPages}
                  totalRecords={paginationMeta.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                />
              ) : null}
            </div>
          </CardBody>
        </Card>

        {filterMetaData?.length ? (
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Notifications"
            handleSubmit={handleFilterSubmit}
            filterData={filterMetaData}
            pageName="UserNotifications"
          />
        ) : null}
      </Container>
    </div>
  );
};

SimpleAllNotifications.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(SimpleAllNotifications);
